import { useState } from 'react';

import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { exchangeAndActivateSharedSessionGrant } from '@backrs_ui/grow-ui/src/redux/sharedSession/exchangeAndActivateSharedSessionGrant.thunk';
import {
  useCreateSharedSessionGrantMutation,
  useGetSharedSessionGrantsByGrantorUserIdQuery,
  useGetSharedSessionGrantsByGranteeUserIdQuery,
  useRevokeSharedSessionGrantMutation,
} from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedSessionGrants.api';
import { selectUserId } from '@backrs_ui/grow-ui/src/redux/user/user';

import {
  Box,
  Button,
  TextField,
  Typography,
  SxProps,
  Theme,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';

export const SharedSessionGrantsPage = () => {
  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectUserId);
  const [granteeEmail, setGranteeEmail] = useState('');
  const [resourceType, setResourceType] = useState('account');

  const { data: grants = [], refetch: refetchGrants } =
    useGetSharedSessionGrantsByGrantorUserIdQuery(
      { grantorUserId: userId?.toString() ?? '' },
      { skip: !userId },
    );

  const { data: grantsReceived = [], refetch: refetchGrantsReceived } =
    useGetSharedSessionGrantsByGranteeUserIdQuery(
      { granteeUserId: userId?.toString() ?? '' },
      { skip: !userId },
    );

  const [createGrant, { isLoading: isCreatingGrant }] = useCreateSharedSessionGrantMutation();
  const [revokeGrant, { isLoading: isRevokingGrant }] = useRevokeSharedSessionGrantMutation();

  const handleCreateGrant = async () => {
    const email = granteeEmail.trim();

    if (!email) {
      alert('Please enter an email');

      return;
    }

    try {
      await createGrant({
        resource_type: resourceType,
        grantee_user_email: email,
      }).unwrap();
      setGranteeEmail('');
      await refetchGrants();
      await refetchGrantsReceived();
    } catch (error) {
      console.error('Failed to create grant:', error);
      alert('Failed to create grant');
    }
  };

  const handleRevokeGrant = async (grantId: string) => {
    try {
      await revokeGrant({ id: grantId }).unwrap();
      await refetchGrants();
      await refetchGrantsReceived();
    } catch (error) {
      console.error('Failed to revoke grant:', error);
      alert('Failed to revoke grant');
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Shared Session Grants Management
      </Typography>

      <Box sx={sectionSx}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Grant Access
        </Typography>
        <Box sx={grantInputSx}>
          <FormControl size="small" sx={{ minWidth: 150, mr: 1 }}>
            <InputLabel>Resource Type</InputLabel>
            <Select
              value={resourceType}
              label="Resource Type"
              onChange={(e) => setResourceType(e.target.value)}
            >
              <MenuItem value="account">Account</MenuItem>
              <MenuItem value="blueprint">Blueprint</MenuItem>
            </Select>
          </FormControl>
          <TextField
            placeholder="Grantee email"
            value={granteeEmail}
            onChange={(e) => setGranteeEmail(e.target.value)}
            size="small"
            sx={{ flex: 1, mr: 1 }}
          />
          <Button
            variant="contained"
            onClick={handleCreateGrant}
            disabled={isCreatingGrant || isRevokingGrant}
          >
            Grant Access
          </Button>
        </Box>
      </Box>

      <Box sx={sectionSx}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Grants Given ({grants.length} grant{grants.length !== 1 ? 's' : ''})
        </Typography>

        {grants.length === 0 ? (
          <Typography>No grants given</Typography>
        ) : (
          grants.map((grant) => (
            <Box key={grant.id} sx={grantItemSx}>
              <Typography variant="body2">
                Grantee: {grant.grantee_user_id} | Granted: {grant.granted_at}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleRevokeGrant(grant.id)}
                disabled={isCreatingGrant || isRevokingGrant}
              >
                Revoke
              </Button>
            </Box>
          ))
        )}
      </Box>

      <Box sx={sectionSx}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Grants Received ({grantsReceived.length} grant{grantsReceived.length !== 1 ? 's' : ''})
        </Typography>

        {grantsReceived.length === 0 ? (
          <Typography>No grants received</Typography>
        ) : (
          grantsReceived.map((grant) => {
            const grantResourceType = grant.share_session?.resource_type ?? 'account';

            return (
              <Box key={grant.id} sx={grantItemSx}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Granted: {grant.granted_at}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() =>
                    dispatch(
                      exchangeAndActivateSharedSessionGrant({
                        grantId: grant.id,
                        resourceType: grantResourceType,
                      }),
                    )
                  }
                  disabled={isCreatingGrant || isRevokingGrant}
                >
                  Activate Session
                </Button>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  marginBottom: backrs.spacing['spacing-4xl'],
});

const grantInputSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  marginBottom: backrs.spacing['spacing-lg'],
});

const grantItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: backrs.spacing['spacing-md'],
  backgroundColor: backrs.colors['Gray/200'],
  borderRadius: '4px',
  marginBottom: backrs.spacing['spacing-sm'],
});
